import React from 'react';
import { Menu, MenuItem } from '../menu';
import styled from '../../styles/styled-components';
import { Button } from '../button';
import { FpsMeter } from './fpsMeter';
import { ShowPresenceToggle } from './showPresenseToggle';

export type ScreenShareToolboxProps = {
  displays: DisplayInfo[];
  selectedDisplay: DisplayInfo;
  onDisplaySelect: (display: DisplayInfo) => void;
  clipboardInput: React.ReactChild;
  refFpsMeter: (fpsMeter: FPSMeter | null) => void;
  showPresenceToUser: boolean;
  onShowPresenceToUserChange: (showPresense: boolean) => void;
  connectionType: React.ReactChild;
  onCtrlAlDel: () => void;
  isMaximized: boolean;
  onChangeIsMaximized: (isMaximized: boolean) => void;
  isZoomedIn: boolean;
  onChangeIsZoomedIn: (isZoomedIn: boolean) => void;
  onFullScreen: () => void;
};

export const ScreenShareToolbox: React.SFC<ScreenShareToolboxProps> = ({
  displays,
  selectedDisplay,
  onDisplaySelect,
  clipboardInput,
  refFpsMeter,
  showPresenceToUser,
  onShowPresenceToUserChange,
  connectionType,
  children,
  onCtrlAlDel,
  isMaximized,
  onChangeIsMaximized,
  isZoomedIn,
  onChangeIsZoomedIn,
  onFullScreen,
  ...rest
}) => (
  <ToolboxMenu horizontal {...rest}>
    {displays.map((display, i) => (
      <DisplayMenuItem key={display.deviceName}>
        <Button
          transparent
          primary={selectedDisplay.deviceName === display.deviceName}
          disabled={selectedDisplay.deviceName === display.deviceName}
          icon="monitor"
          content={`Display ${i + 1}`}
          onClick={() => onDisplaySelect(display)}
        />
      </DisplayMenuItem>
    ))}
    <ClipboadMenuItem>{clipboardInput}</ClipboadMenuItem>
    {process.env.SYNCRO && (
      <ToggleMenuItem>
        <ShowPresenceToggle
          showPresenceToUser={showPresenceToUser}
          onShowPresenceToUserChange={onShowPresenceToUserChange}
        />
      </ToggleMenuItem>
    )}
    <FPSMenuItem>
      <FpsMeter refFpsMeter={refFpsMeter} />
    </FPSMenuItem>
    <CommandButtonsMenuItem>
      <CtrlAltDelButton
        content="CTRL-ALT-DEL"
        onClick={onCtrlAlDel}
        size="small"
        aria-label="Send Control-Alt-Delete"
      />
      <Button
        icon={isMaximized ? 'minimize2' : 'maximize2'}
        onClick={() => onChangeIsMaximized(!isMaximized)}
        size="small"
        aria-label={isMaximized ? 'Maximize window' : 'Minimize window'}
      />
      <Button
        icon="maximize"
        onClick={onFullScreen}
        size="small"
        aria-label="Go fullscreen"
      />
      <Button
        icon={isZoomedIn ? 'zoomOut' : 'zoomIn'}
        onClick={() => onChangeIsZoomedIn(!isZoomedIn)}
        size="small"
        aria-label={isZoomedIn ? 'Zoom out' : 'Zoom in'}
      />
    </CommandButtonsMenuItem>
    {/* <OtherMenuItem>{children}</OtherMenuItem> */}
    <ConnectionTypeMenuItem>{connectionType}</ConnectionTypeMenuItem>
  </ToolboxMenu>
);

const ToolboxMenu = styled(Menu)`
  height: 50px;
  border-bottom: 1px solid ${p => p.theme.dividerColor};
`;

const DisplayMenuItem = MenuItem.extend`
  flex: 1 0 auto;
  display: flex;
  align-items: center;
`;

const ClipboadMenuItem = MenuItem.extend`
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 20px;
`;

const ToggleMenuItem = MenuItem.extend`
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 20px;
`;

const FPSMenuItem = MenuItem.extend`
  position: relative;
  flex: 0 0 110px;
`;

const CommandButtonsMenuItem = MenuItem.extend`
  flex: 0 0 250px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px;
`;

const CtrlAltDelButton = styled(Button)`
  padding: 5px 10px;
  padding-bottom: 4px;
`;

const OtherMenuItem = MenuItem.extend``;

export type ScreenShareHeaderProps = {
  connectionType: React.ReactChild;
};

export const ScreenShareHeader: React.SFC<ScreenShareHeaderProps> = ({
  connectionType,
  ...rest
}) => (
  <HeaderMenu horizontal {...rest}>
    <TitleMenuItem>Remote Desktop</TitleMenuItem>
    <ConnectionTypeMenuItem>{connectionType}</ConnectionTypeMenuItem>
  </HeaderMenu>
);

const HeaderMenu = styled(Menu)`
  height: 50px;
  border-bottom: 1px solid ${p => p.theme.dividerColor};
`;

const TitleMenuItem = MenuItem.extend`
  display: flex;
  align-items: center;
  flex: 1 0 auto;
  padding: 0 20px;
`;

const ConnectionTypeMenuItem = MenuItem.extend`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  padding: 0 20px;
`;
